import React, { Dispatch, FC, SetStateAction } from "react";
import { isBefore } from "date-fns";
import { generatePath, useNavigate } from "react-router-dom";
import _camelCase from "lodash/camelCase";
import { StickyCTABar } from "component-library";
import { useTranslation } from "i18n";
import { ISelfTapeRequestStatus, MediaRequestType } from "shared-types";
import { ISelfTapeRequest } from "shared/models/selfTapeRequest";
import { useGetCancelButton } from "../helpers";
import { AgentActionFooter } from "../SelfTapeRequestAgentParts";
import { SELF_TAPE_UPLOAD_FULL_PATH } from "config/common";

interface Props {
  selfTapeRequest: ISelfTapeRequest;
  clientName: string;
  setAcceptRequestModalIsOpen: Dispatch<SetStateAction<boolean>>;
  isAgent?: boolean;
}

const SelfTapeRequestActionFooter: FC<React.PropsWithChildren<Props>> = ({
  selfTapeRequest,
  clientName,
  setAcceptRequestModalIsOpen,
  isAgent,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cancelButton = useGetCancelButton(t("common:button.label.back"));

  const isRejected = selfTapeRequest.status === ISelfTapeRequestStatus.Rejected;
  const isExpired = isBefore(new Date(selfTapeRequest.expiryDate), new Date());

  const ctaHidden =
    isExpired || isRejected || Boolean(selfTapeRequest.submissionMedia.length);

  const isSubmitDisabled = selfTapeRequest.isPublished;

  const getSubmitButtonConfig = () => {
    const navigateToSelfTapeUpload = () =>
      navigate(
        generatePath(SELF_TAPE_UPLOAD_FULL_PATH, {
          performerRequestId: selfTapeRequest.performerRequestId,
        })
      );

    switch (selfTapeRequest.status) {
      case ISelfTapeRequestStatus.Awaiting:
        return {
          text: t("common:selfTapes.request.button.responseToRequest"),
          onClick: () => setAcceptRequestModalIsOpen(true),
          disabled: isSubmitDisabled,
        };
      case ISelfTapeRequestStatus.Accepted:
        return {
          text: t(
            `common:selfTapes.request.submit.${_camelCase(
              MediaRequestType[selfTapeRequest.mediaRequestType]
            )}`
          ),
          onClick: navigateToSelfTapeUpload,
          disabled: isSubmitDisabled,
        };
      default:
        return {
          disabled: isSubmitDisabled,
          text: t("common:selfTapes.request.button.submit"),
          onClick: navigateToSelfTapeUpload,
        };
    }
  };

  if (ctaHidden) return null;

  const submitButtonConfig = getSubmitButtonConfig();

  return (
    <>
      {isAgent ? (
        <AgentActionFooter
          selfTapeRequestId={selfTapeRequest.performerRequestId}
          clientName={clientName}
          submitButtonConfig={submitButtonConfig}
          isAccepted={
            selfTapeRequest.status === ISelfTapeRequestStatus.Accepted
          }
          isTagminUser={selfTapeRequest.isTagminUser}
          isTagTapesUser={selfTapeRequest.isTagTapesUser}
          forwardedToPerformer={selfTapeRequest.forwardedToPerformer}
        />
      ) : (
        <StickyCTABar
          prev={cancelButton}
          next={submitButtonConfig}
          ctaBarAdditionalClassName="max-w-[1440px]"
          isSticky
        />
      )}
    </>
  );
};

export default SelfTapeRequestActionFooter;
